import { useState, useEffect, useMemo } from 'react';
import { BACKEND, MEDIA_STUDIO_API_KEY } from '../constants';
const NAVY = '#1E293B';
const GOLD = '#BA7517';
const PLAT_COLORS = {
  linkedin:  { bg: '#E6F1FB', color: '#185FA5', icon: '💼' },
  tiktok:    { bg: '#f0f0f0', color: '#333',    icon: '🎵' },
  instagram: { bg: '#FBEAF0', color: '#D4537E', icon: '📸' },
  bluesky:   { bg: '#E6F1FB', color: '#185FA5', icon: '🦋' },
  facebook:  { bg: '#E6F1FB', color: '#185FA5', icon: '👥' },
  x:         { bg: '#f0f0f0', color: '#333',    icon: '𝕏' },
  threads:   { bg: '#EEEDFE', color: '#534AB7', icon: '🧵' },
  youtube:   { bg: '#FBEAF0', color: '#D4537E', icon: '▶️' },
  mastodon:  { bg: '#EEEDFE', color: '#534AB7', icon: '🐘' },
};
function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + ' min ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + ' hr ago';
  return Math.floor(hrs / 24) + 'd ago';
}
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
function todayStr() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function StatCard({ icon, iconBg, iconColor, num, label, sub }) {
  return (
    <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
      <div style={{ width: 28, height: 28, borderRadius: 7, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8, fontSize: 14, color: iconColor }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 500, color: '#0f172a', lineHeight: 1 }}>{num}</div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
function PipelineStep({ num, name, desc, btnLabel, color, textColor, onClick, showArrow }) {
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ background: color, borderRadius: 8, padding: 10, textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 500, color: textColor }}>{num}</div>
        <div style={{ fontSize: 11, fontWeight: 500, color: textColor, marginTop: 2 }}>{name}</div>
        <div style={{ fontSize: 10, color: textColor, marginTop: 1, opacity: 0.8 }}>{desc}</div>
        <button onClick={onClick} style={{ marginTop: 6, fontSize: 10, background: 'rgba(255,255,255,0.35)', border: 'none', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', color: textColor, fontFamily: 'inherit' }}>{btnLabel}</button>
      </div>
      {showArrow && <div style={{ position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94a3b8', zIndex: 1 }}>›</div>}
    </div>
  );
}
export default function Dashboard({ activityLog, scheduledPosts, setMainTab }) {
  const [ideas, setIdeas] = useState([]);
  const [findings, setFindings] = useState([]);
  const [bufferCounts, setBufferCounts] = useState({});
  const [assets, setAssets] = useState([]);
  const [upcomingPosts, setUpcomingPosts] = useState([]);
  const [publishHistory, setPublishHistory] = useState([]);

  useEffect(() => {
    const headers = { 'x-api-key': MEDIA_STUDIO_API_KEY };
    Promise.allSettled([
      fetch(`${BACKEND}/api/content?type=ideas`, { headers }).then(r => r.json()),
      fetch(`${BACKEND}/api/content?type=findings`, { headers }).then(r => r.json()),
      fetch(`${BACKEND}/api/buffer-queue`, { headers }).then(r => r.json()),
      fetch(`${BACKEND}/api/assets`, { headers }).then(r => r.json()),
      fetch(`${BACKEND}/scheduler/posts?status=pending`, { headers }).then(r => r.json()),
      fetch(`${BACKEND}/api/buffer-queue/exported`, { headers }).then(r => r.json()),
    ]).then(([ideasR, findingsR, bufferR, assetsR, postsR, exportedR]) => {
      if (ideasR.status === 'fulfilled' && ideasR.value.success) setIdeas(ideasR.value.items);
      if (findingsR.status === 'fulfilled' && findingsR.value.success) setFindings(findingsR.value.items);
      if (bufferR.status === 'fulfilled' && bufferR.value.success) setBufferCounts(bufferR.value.counts || {});
      if (assetsR.status === 'fulfilled' && Array.isArray(assetsR.value)) setAssets(assetsR.value);
      if (postsR.status === 'fulfilled' && postsR.value.posts) {
        // scheduled_at is a Postgres BIGINT, which node-pg returns as a string
        // ("1787514480000"). new Date("1787514480000") is Invalid Date, so convert.
        // Overdue 'pending' rows are not "upcoming", so only future posts show here.
        const nowMs = Date.now();
        setUpcomingPosts(postsR.value.posts
          .map(p => ({
            id: p.id, title: p.content ? p.content.slice(0, 60) : 'Untitled post',
            platform: p.platform, scheduledAt: Number(p.scheduled_at), status: 'ready',
          }))
          .filter(p => Number.isFinite(p.scheduledAt) && p.scheduledAt >= nowMs)
          .slice(0, 4));
      }
      if (exportedR.status === 'fulfilled' && exportedR.value.success) {
        setPublishHistory(exportedR.value.posts);
      }
    });
  }, []);

  const viralCount = findings.length;
  const queueCount = Object.values(bufferCounts).reduce((a, b) => a + b, 0);
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const publishedCount = useMemo(() => {
    return activityLog.filter(e => e.type === 'real' && e.status !== 'err' && new Date(e.ts) >= monthStart).length;
  }, [activityLog]);
  const assetsThisMonth = useMemo(() => {
    return assets.filter(a => a.created_at && new Date(a.created_at) >= monthStart).length;
  }, [assets]);
  const topFindings = useMemo(() => [...findings].sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 3), [findings]);
  const stageNew       = ideas.filter(i => i.status === 'review').length;
  const stageApproved  = ideas.filter(i => i.status === 'approved').length;
  const stageRejected  = ideas.filter(i => i.status === 'rejected').length;
  const stageReady     = queueCount;
  const stagePublished = ideas.filter(i => i.status === 'published').length;
  const stageArchived  = useMemo(() => { try { return JSON.parse(localStorage.getItem('sh_ci_archive') || '[]').length; } catch { return 0; } }, []);
  const recentActivity = activityLog.slice(0, 3);
  function viralScoreStyle(score) {
    if (score >= 90) return { bg: '#FEF3C7', numColor: '#854F0B', tagColor: '#BA7517' };
    if (score >= 85) return { bg: '#E6F1FB', numColor: '#0C447C', tagColor: '#185FA5' };
    return { bg: '#EEEDFE', numColor: '#3C3489', tagColor: '#534AB7' };
  }
  function fmtTime(ts) {
    if (!ts || Number.isNaN(new Date(ts).getTime())) return '';
    const d = new Date(ts), t = new Date(); t.setHours(0,0,0,0);
    const tm = new Date(t); tm.setDate(tm.getDate()+1);
    const dc = new Date(d); dc.setHours(0,0,0,0);
    const pre = dc.getTime()===t.getTime() ? 'Today' : dc.getTime()===tm.getTime() ? 'Tomorrow' : d.toLocaleDateString('en-US',{month:'short',day:'numeric'});
    return pre + ', ' + d.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',hour12:true});
  }
  function fmtDate(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  const panel = { background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, padding: 14 };
  const ph = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 };
  const pt = { fontSize: 13, fontWeight: 500, color: '#0f172a' };
  const va = { fontSize: 11, color: GOLD, cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit' };
  const dv = { borderBottom: '0.5px solid #e2e8f0' };
  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 500, color: '#0f172a' }}>{greeting()}, Gopu</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Here's your content overview for today.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#64748b' }}>📅 {todayStr()}</div>
          <button onClick={() => setMainTab('create')} style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>+ New Content</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 18 }}>
        <StatCard icon="🔥" iconBg="#FEF3C7" iconColor={GOLD}      num={viralCount}    label="Viral opportunities" sub="High potential ideas" />
        <StatCard icon="📋" iconBg="#E6F1FB" iconColor="#185FA5"   num={queueCount}    label="Content in queue"    sub="Across 7 platforms" />
        <StatCard icon="🎨" iconBg="#EAF3DE" iconColor="#3B6D11"   num={assetsThisMonth} label="Assets generated"   sub="This month" />
        <StatCard icon="📤" iconBg="#EEEDFE" iconColor="#534AB7"   num={publishedCount} label="Published posts"    sub="This month" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div style={panel}>
          <div style={ph}><span style={pt}>🔥 Top viral opportunities</span><button style={va} onClick={() => { setMainTab('research'); }}>View all</button></div>
          {topFindings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 12 }}>
              Run Research to find viral opportunities<br/>
              <button onClick={() => setMainTab('research')} style={{ marginTop: 8, fontSize: 11, background: GOLD, color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>Go to Research</button>
            </div>
          ) : topFindings.map((f, i) => {
            const score = Math.round((f.score || 7) * 10);
            const st = viralScoreStyle(score);
            return (
              <div key={f.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', ...(i < topFindings.length-1 ? dv : {}) }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: st.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: st.numColor, lineHeight: 1 }}>{score}</span>
                  <span style={{ fontSize: 8, fontWeight: 500, color: st.tagColor, textTransform: 'uppercase' }}>score</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a' }}>{f.title || f.trend || 'Untitled'}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.core || f.whyItWorks || ''}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 10, color: '#64748b' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#0A66C2', display: 'inline-block' }}/>
                    Trending on {f.platform || 'LinkedIn'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={panel}>
          <div style={ph}><span style={pt}>📅 Upcoming schedule</span><button style={va} onClick={() => setMainTab('calendar')}>View calendar</button></div>
          {upcomingPosts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 12 }}>
              No posts scheduled yet<br/>
              <button onClick={() => setMainTab('review-publish')} style={{ marginTop: 8, fontSize: 11, background: NAVY, color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>Go to Review & Publish</button>
            </div>
          ) : upcomingPosts.map((p, i) => {
            const plat = PLAT_COLORS[(p.platform||'').toLowerCase()] || { bg: '#f0f0f0', color: '#333', icon: '📄' };
            const isReady = p.status === 'ready' || p.status === 'approved';
            return (
              <div key={p.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', ...(i < upcomingPosts.length-1 ? dv : {}) }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: plat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}>{plat.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title || p.caption || 'Untitled post'}</div>
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{fmtTime(p.scheduledAt)}</div>
                </div>
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: isReady ? '#EAF3DE' : '#FEF3C7', color: isReady ? '#3B6D11' : '#854F0B', whiteSpace: 'nowrap' }}>{isReady ? 'Ready' : 'Draft'}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div style={panel}>
        <div style={ph}><span style={pt}>Content pipeline</span><button style={va} onClick={() => setMainTab('research')}>View all</button></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 8 }}>
          <PipelineStep num={stageNew}       name="New"        desc="Awaiting review"  btnLabel="Go to Repurpose" color="#FEF3C7" textColor="#854F0B" onClick={() => setMainTab('repurpose')} showArrow />
          <PipelineStep num={stageApproved}  name="Approved"   desc="Content drafted"  btnLabel="Go to Repurpose" color="#E6F1FB" textColor="#0C447C" onClick={() => setMainTab('repurpose')} showArrow />
          <PipelineStep num={stageRejected}  name="Rejected"   desc="Not pursued"      btnLabel="Go to Repurpose" color="#FEF2F2" textColor="#991B1B" onClick={() => setMainTab('repurpose')} showArrow={false} />
          <PipelineStep num={stageReady}     name="Ready"      desc="In queue"         btnLabel="Go to Queue"     color="#EEEDFE" textColor="#3C3489" onClick={() => setMainTab('queue')} showArrow />
          <PipelineStep num={stagePublished} name="Published"  desc="Live on platforms" btnLabel="View Analytics" color="#EAF3DE" textColor="#27500A" onClick={() => setMainTab('log')} showArrow />
          <PipelineStep num={stageArchived}  name="Archived"   desc="Done & stored"    btnLabel="View Archive"    color="#F1EFE8" textColor="#444441" onClick={() => setMainTab('archive')} showArrow={false} />
        </div>
      </div>
      <div style={{ ...panel, marginTop: 14 }}>
        <div style={ph}><span style={pt}>📤 Publishing history</span><span style={{ fontSize: 10, color: '#94a3b8' }}>Buffer-exported posts · LinkedIn/Facebook/TikTok/Pinterest/Bluesky/Mastodon</span></div>
        {publishHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px 0', color: '#94a3b8', fontSize: 12 }}>
            No exported posts yet — export happens automatically once a platform queue hits 10, or manually from Queue.
          </div>
        ) : publishHistory.map((p, i) => {
          const plat = PLAT_COLORS[(p.platform||'').toLowerCase()] || { bg: '#f0f0f0', color: '#333', icon: '📄' };
          return (
            <div key={p.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', ...(i < publishHistory.length-1 ? dv : {}) }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: plat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}>{plat.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title || 'Untitled post'}</div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 1, textTransform: 'capitalize' }}>{p.platform}</div>
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(p.exported_at)}</div>
            </div>
          );
        })}
      </div>
      {recentActivity.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          {recentActivity.map((entry, i) => {
            const isFail = entry.status === 'err', isReal = entry.type === 'real';
            return (
              <div key={entry.id || i} style={{ ...panel, flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: isFail?'#FEF2F2':isReal?'#EAF3DE':'#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}>{isFail?'❌':isReal?'✅':'⚠️'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 500, color: '#0f172a' }}>{entry.platform}</div>
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.msg}</div>
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>{timeAgo(entry.ts)}</div>
              </div>
            );
          })}
        </div>
      )}
      {recentActivity.length === 0 && (
        <div style={{ ...panel, marginTop: 14, textAlign: 'center', padding: 18, color: '#94a3b8', fontSize: 12 }}>
          No recent activity yet — start by running Research or publishing a post.
        </div>
      )}
    </div>
  );
}
